import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { ConfigContext, UserContext } from '../../context';
import { AppTab, ChannelAppTabAction, useStoriesCardsUserFeedStatisticQuery } from '../../services';
import {
  DropdownMenu,
  Popup,
  getAppTabHrefByActionType,
  getCssVar,
  getLocale,
  useFreeLoginPopup,
} from '../../shared';

import classNames from 'classnames';
import sprite from '../../assets/icons/sprite.svg';
import dropdownMenuClasses from '../../shared/components/DropdownMenu/DropdownMenu.module.scss';
import classes from './Menu.module.scss';

interface MenuProps {
  appTabs: AppTab[];
  hasPostInStories: boolean;
  channelId: number;
  userId: number;
}

interface MenuItemClickArgs {
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>;
  type: ChannelAppTabAction;
}

interface FrontDoorTabClickArgs {
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>;
  externalUrl: string | null;
}

export const Menu: FunctionComponent<MenuProps> = memo(
  ({ appTabs, hasPostInStories, channelId, userId }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { isAnonymous } = useContext(UserContext).userProfile;

    const { appTabsSecondaryOrders, frontDoor, userDirectory, bookmarks, navMenu } =
      useContext(ConfigContext).config.elements;

    const { localiseKey: moreLocaliseKey = 'common.more', iconUrl: moreIconUrl } =
      navMenu.more ?? {};

    const appTabsSecondaryOrdersWithBookmarks = useMemo(
      () => [...appTabsSecondaryOrders, 'bookmarks'],
      [appTabsSecondaryOrders]
    );

    const { tabs: frontDoorTabs = [] } = { ...frontDoor };

    const {
      showAsPrimaryTab: userDirectoryShowAsPrimaryTab,
      localiseKey: userDirectoryLocaliseKey,
    } = userDirectory;

    const { showAsPrimaryTab: bookmarksShowAsPrimaryTab } = bookmarks;

    const timeStamp = useMemo(() => Date.now(), []);

    const visibleFrontDoorTabs = useMemo(
      () =>
        frontDoorTabs.filter(
          ({ channelsExclude, items, externalUrl }) =>
            !channelsExclude.includes(channelId) && (items.length || externalUrl?.length)
        ),
      [channelId, frontDoorTabs]
    );

    const { data: stats } = useStoriesCardsUserFeedStatisticQuery(
      { channelId, userId },
      { skip: !appTabsSecondaryOrders.includes('myContent') }
    );

    const renderMyContentTab = useMemo(
      () =>
        Boolean(
          (stats && Object.values(stats).find((value) => Boolean(value))) || hasPostInStories
        ),
      [hasPostInStories, stats]
    );

    const [secondaryMenuOpen, setSecondaryMenuOpen] = useState<boolean>(false);

    const myContentClick = useCallback(() => {
      setSecondaryMenuOpen(false);
      navigate('/profile');
    }, [navigate]);

    const userDirectoryClick = useCallback(() => {
      setSecondaryMenuOpen(false);
      navigate('/userDirectory');
    }, [navigate]);

    const bookmarksClick = useCallback(() => {
      setSecondaryMenuOpen(false);

      if (isAnonymous) {
        freeLoginHandler();
        return;
      }

      navigate('/bookmarks');
    }, [freeLoginHandler, isAnonymous, navigate]);

    const getSecondaryTab = useCallback(
      (tab: string) => {
        switch (true) {
          case tab === 'myContent' && renderMyContentTab:
            return (
              <button
                key={tab}
                type={'button'}
                className={dropdownMenuClasses['dropdown-menu__item']}
                onClick={myContentClick}
              >
                {t('secondaryTabs.my-content')}
              </button>
            );
          case tab === 'userDirectory' && !userDirectoryShowAsPrimaryTab:
            return (
              <button
                key={tab}
                type={'button'}
                className={dropdownMenuClasses['dropdown-menu__item']}
                onClick={userDirectoryClick}
              >
                {t(userDirectoryLocaliseKey)}
              </button>
            );
          case tab === 'bookmarks' && !bookmarksShowAsPrimaryTab:
            return (
              <button
                key={tab}
                type={'button'}
                className={dropdownMenuClasses['dropdown-menu__item']}
                onClick={bookmarksClick}
              >
                {t('secondaryTabs.bookmarks')}
              </button>
            );
          default:
            return null;
        }
      },
      [
        bookmarksClick,
        bookmarksShowAsPrimaryTab,
        myContentClick,
        renderMyContentTab,
        t,
        userDirectoryClick,
        userDirectoryLocaliseKey,
        userDirectoryShowAsPrimaryTab,
      ]
    );

    const appTabsSecondaryContent = useMemo(
      () =>
        appTabsSecondaryOrdersWithBookmarks
          .map((tab) => getSecondaryTab(tab))
          .filter((tab) => Boolean(tab)),
      [appTabsSecondaryOrdersWithBookmarks, getSecondaryTab]
    );

    const menuItemClick = useCallback(
      ({ event, type }: MenuItemClickArgs) => {
        if (
          isAnonymous &&
          [ChannelAppTabAction.CHATS, ChannelAppTabAction.BOOKMARKS].includes(type)
        ) {
          event.preventDefault();
          freeLoginHandler();
        }
      },
      [freeLoginHandler, isAnonymous]
    );

    const frontDoorTabClick = useCallback(({ event, externalUrl }: FrontDoorTabClickArgs) => {
      if (externalUrl) {
        event.preventDefault();
        window.open(externalUrl, '_blank', 'noopener, noreferrer');
      }
    }, []);

    return (
      <>
        <ul className={classes['menu']}>
          {appTabs.map(({ action, title, iconUrl }) => {
            const { type, storyId } = action;

            const label = title.find(({ language }) => language === getLocale())?.localization;

            return (
              <li key={label}>
                <NavLink
                  to={getAppTabHrefByActionType({ actionType: type, storyId })}
                  className={({ isActive }) =>
                    classNames(classes['menu__item'], {
                      [classes['menu__item--active']]: isActive,
                    })
                  }
                  onClick={(event) => menuItemClick({ event, type })}
                >
                  {iconUrl && (
                    <div
                      className={classes['menu__item-icon']}
                      style={{
                        mask: `url('${iconUrl}?date=${timeStamp}') no-repeat center`,
                        WebkitMask: `url('${iconUrl}?date=${timeStamp}') no-repeat center`,
                        maskSize: 'cover',
                        WebkitMaskSize: 'cover',
                      }}
                    />
                  )}

                  <span className={classes['menu__item-label']}>{label}</span>
                </NavLink>
              </li>
            );
          })}

          {visibleFrontDoorTabs.map(({ tabIconUrl, titleLocaliseKey, externalUrl }, index) => (
            <li key={`frontDoorTab${index + 1}`}>
              <NavLink
                to={`/frontDoorTab${index + 1}`}
                className={({ isActive }) =>
                  classNames(classes['menu__item'], {
                    [classes['menu__item--active']]: isActive,
                  })
                }
                onClick={(event) => frontDoorTabClick({ event, externalUrl })}
              >
                {tabIconUrl && (
                  <div
                    className={classes['menu__item-icon']}
                    style={{
                      mask: `url('${tabIconUrl}') no-repeat center`,
                      WebkitMask: `url('${tabIconUrl}') no-repeat center`,
                      maskSize: 'cover',
                      WebkitMaskSize: 'cover',
                    }}
                  />
                )}
                {titleLocaliseKey && (
                  <span className={classes['menu__item-label']}>{t(titleLocaliseKey)}</span>
                )}
                {externalUrl && (
                  <svg width={16} height={16} className={classes['menu__item-arrow-icon']}>
                    <use href={`${sprite}#arrow-rounded-right-up`} />
                  </svg>
                )}
              </NavLink>
            </li>
          ))}

          {Boolean(appTabsSecondaryContent.length) && (
            <li>
              <Popup
                isOpen={secondaryMenuOpen}
                setIsOpen={setSecondaryMenuOpen}
                {...(!moreIconUrl && { iconId: 'more' })}
                iconUrl={moreIconUrl}
                label={t(moreLocaliseKey)}
                labelClassName={classes['menu__item-label']}
                className={classes['menu__item']}
                color={getCssVar('--nav-menu-link-text-color')}
                hoverColor={getCssVar('--nav-menu-link-text-hover-color')}
                bodyTop={'3rem'}
                bodyLeft={'1rem'}
                body={<DropdownMenu width={'13rem'} content={appTabsSecondaryContent} />}
              />
            </li>
          )}
        </ul>

        {freeLoginModal}
      </>
    );
  }
);
